/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * A-2 (S711 review) — ONE reconnection signal, several handlers.
 *
 * Losing the connection was detected (lib/network-status.ts) and announced
 * (OfflineIndicator), and nothing anywhere recovered from it: every screen
 * stayed in the state the outage left it in, under a banner saying the
 * connection was back. Detection is generic and was built once; recovery is
 * specific to each screen -- the cart re-requests the payment library and
 * re-reads its data, checkout resolves its payment session -- and no screen was
 * ever given the signal.
 *
 * This module is the signal. It knows nothing about any screen: screens
 * subscribe with `useRecovery()` and each recovers in its own way. It is fired
 * by the offline banner when the browser reports the connection is back, and by
 * the banner's Retry button, so the button's label is true.
 */

type RecoveryListener = () => void;

const listeners = new Set<RecoveryListener>();

/** Subscribe to recovery requests. Returns the unsubscribe function. */
export function onRecovery(listener: RecoveryListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Ask every mounted screen to recover. A listener that throws is contained, so
 * one screen's failure cannot stop another screen from recovering.
 */
export function requestRecovery(): void {
  for (const listener of Array.from(listeners)) {
    try {
      listener();
    } catch {
      // A recovery handler reports its own failure on screen; nothing to add.
    }
  }
}

/** Test seam: how many screens are listening. */
export function recoveryListenerCount(): number {
  return listeners.size;
}
